import * as React from 'react';
import { Dispatch, SetStateAction } from 'react';
import Typography from 'antd/lib/typography';
import Button from 'antd/lib/button';
import { ArrowRightOutlined, LockFilled } from '@ant-design/icons';
import { displayTextForSetupStepTypeIntegrations } from '../../helpers/displayHelpers';
import { SetupStepType, SetupStepsCallerContext } from '../../typescriptHelpers';
import { IntegrationCapability } from '../../types';
import { antGold, antGrey } from '../styles/globalStyles';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppHasSelectCapabilitiesConfigured } from '../../selectors/selectors';
import { IntegrationsSetupList } from '../sharedComponents/IntegrationsSetupList';

type RelatedIntegrationsSetupProps = {
    appId: string;
    orgId: string;
    showIntegrationSetup: Dispatch<SetStateAction<boolean>>;
    currentStepType: SetupStepType;
    relatedCapabilities: IntegrationCapability[];
    context?: SetupStepsCallerContext;
};
export const RelatedIntegrationsSetup = (props: RelatedIntegrationsSetupProps) => {
    const { appId, orgId, showIntegrationSetup, relatedCapabilities, currentStepType, context } = props;

    const capabilitiesAreComplete = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, relatedCapabilities),
    );

    const backgroundColor = capabilitiesAreComplete ? 'rgba(110, 199, 139, 0.15)' : 'rgba(106, 127, 219, 0.15)';
    return (
        <div style={{ height: 'auto', backgroundColor: 'white' }}>
            <div
                style={{
                    backgroundColor,
                    padding: 70,
                    display: 'flex',
                    gap: '10%',
                    minHeight: 'calc(100vh - 168px - 88px)', // height of header + footer
                }}
            >
                <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 36, paddingBottom: 150 }}>
                    <IntegrationsSetupList
                        appId={appId}
                        orgId={orgId}
                        relatedCapabilities={relatedCapabilities}
                        shouldShowTitle
                        shouldShowUnrelatedCapabilities
                        context={context}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <InfoSection
                        currentStepType={currentStepType}
                        showIntegrationSetup={showIntegrationSetup}
                        isComplete={capabilitiesAreComplete}
                    />
                </div>
            </div>
        </div>
    );
};

type InfoSectionProps = {
    currentStepType: SetupStepType;
    showIntegrationSetup: Dispatch<SetStateAction<boolean>>;
    isComplete: boolean;
};
const InfoSection = (props: InfoSectionProps) => {
    const { currentStepType, showIntegrationSetup, isComplete } = props;
    const completeString = 'Great, you’re connected!\n\nConnect other types of integrations now?';
    const infoString = isComplete ? completeString : displayTextForSetupStepTypeIntegrations(currentStepType);
    const buttonText = isComplete ? 'Skip for now' : 'Do this later';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 45, alignItems: 'end' }}>
            {infoString && (
                <Typography.Paragraph
                    style={{
                        fontSize: 24,
                        color: antGrey.grey8,
                        whiteSpace: 'pre-line',
                        textAlign: 'end',
                        marginBottom: 0,
                    }}
                >
                    {infoString}
                </Typography.Paragraph>
            )}
            <div>
                <Button
                    type="primary"
                    ghost
                    size="large"
                    icon={<ArrowRightOutlined />}
                    style={{ backgroundColor: '#FFF' }}
                    onClick={() => showIntegrationSetup(false)}
                >
                    {buttonText}
                </Button>
            </div>
            <SecurityNote />
        </div>
    );
};

const SecurityNote = () => {
    return (
        <Typography.Text style={{ textAlign: 'end' }}>
            <LockFilled style={{ color: antGold.gold7, marginRight: 10 }} size={15} />
            All integrations are connected via official APIs, authenticated with the most modern option offered. We are
            SOC 2 certified.
        </Typography.Text>
    );
};
